<template>
  <div class="branding-configuration" data-testid="branding-configuration">
    <!-- Breadcrumb: back to the Configuration panel grid. -->
    <ExBreadcrumbs class="branding-configuration__breadcrumbs" :breadcrumbsList="breadcrumbItems" />

    <div class="branding-configuration__section">
      <!-- Section header. -->
      <div class="branding-configuration__header">
        <h2 class="branding-configuration__title">Branding</h2>
        <p class="branding-configuration__subtitle">
          Customize how your organization appears across exIQtive.
        </p>
      </div>

      <hr class="branding-configuration__divider" />

      <p
        v-if="loadError"
        class="branding-configuration__message branding-configuration__message--error"
        role="alert"
        data-testid="branding-load-error"
      >
        {{ loadError }}
      </p>

      <!-- Account logo row. -->
      <div class="branding-configuration__row">
        <div class="branding-configuration__label-block">
          <span id="branding-account-logo-label" class="branding-configuration__name">
            Account logo
          </span>
        </div>

        <div
          class="branding-configuration__control"
          role="group"
          aria-labelledby="branding-account-logo-label"
          :aria-busy="busy ? 'true' : 'false'"
        >
          <p
            v-if="loading"
            class="branding-configuration__hint"
            role="status"
            data-testid="branding-logo-loading"
          >
            Loading logo…
          </p>

          <div v-else-if="logoUrl" class="branding-configuration__current" data-testid="branding-logo-set">
            <div class="branding-configuration__preview">
              <img
                class="branding-configuration__preview-image"
                :src="logoUrl"
                alt="Current account logo"
                data-testid="branding-logo-preview"
              />
            </div>
            <div class="branding-configuration__actions">
              <StandardButton
                type="magenta"
                variant="secondary"
                size="normal"
                label="Replace"
                :disabled="busy"
                :loaderLeft="uploading"
                data-testid="branding-logo-replace"
                @click="openFilePicker"
              />
              <StandardButton
                type="red"
                variant="secondary"
                size="normal"
                label="Remove"
                :disabled="busy"
                data-testid="branding-logo-remove"
                @click="showRemoveDialog = true"
              />
            </div>
          </div>

          <button
            v-else
            type="button"
            class="branding-configuration__dropzone"
            :class="{
              'branding-configuration__dropzone--dragover': isDragging,
              'branding-configuration__dropzone--error': !!uploadError,
            }"
            :disabled="busy"
            aria-describedby="branding-logo-hint"
            data-testid="branding-logo-dropzone"
            @click="openFilePicker"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <span class="branding-configuration__dropzone-icon" aria-hidden="true">
              <img :src="uploadCloudSrc" alt="" width="20" height="20" />
            </span>
            <span class="branding-configuration__dropzone-action">
              <span class="branding-configuration__dropzone-link">Click to upload</span>
              <span class="branding-configuration__dropzone-or">or drag and drop</span>
            </span>
            <span id="branding-logo-hint" class="branding-configuration__dropzone-hint">
              {{ UPLOAD_HINT }}
            </span>
          </button>

          <div
            v-if="uploadError"
            class="branding-configuration__feedback"
            role="alert"
            data-testid="branding-logo-error"
          >
            <span class="branding-configuration__error-text">{{ uploadError.message }}</span>
            <StandardButton
              v-if="uploadError.retryable"
              type="magenta"
              variant="text-primary"
              size="small"
              label="Try again"
              :disabled="busy"
              data-testid="branding-logo-retry"
              @click="retryUpload"
            />
          </div>

          <input
            ref="fileInput"
            type="file"
            class="branding-configuration__file-input"
            :accept="ACCEPT_ATTRIBUTE"
            tabindex="-1"
            aria-label="Choose account logo file"
            data-testid="branding-logo-input"
            @change="handleFileInput"
          />
        </div>
      </div>
    </div>

    <RemoveLogoDialog
      v-model="showRemoveDialog"
      :loading="removing"
      @confirm="confirmRemove"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import RemoveLogoDialog from "@components/RemoveLogoDialog/RemoveLogoDialog.vue";
import { deleteAccountLogo, getAccountData, uploadAccountLogo } from "@/api";

const MAX_LOGO_MB = 5;
const MAX_LOGO_BYTES = MAX_LOGO_MB * 1024 * 1024;
const ACCEPTED_TYPES = ["image/svg+xml", "image/png", "image/jpeg", "image/webp"];
const ACCEPT_ATTRIBUTE = ACCEPTED_TYPES.join(",");
const UPLOAD_HINT = `SVG, PNG, JPG or WebP — max. ${MAX_LOGO_MB} MB. Transparent PNG or SVG recommended.`;
const INVALID_FILE_MESSAGE = `This file can’t be used. Upload an SVG, PNG, JPG or WebP file up to ${MAX_LOGO_MB} MB.`;
const UPLOAD_FAILED_MESSAGE = "Upload failed. Your logo hasn’t changed.";
const REMOVE_FAILED_MESSAGE = "Couldn’t remove your logo. Your logo hasn’t changed.";

const props = defineProps({
  /**
   * Account whose logo is managed. Falls back to the `currentAccountID` localStorage
   * value used by the rest of the Settings flow.
   */
  accountId: {
    type: [Number, String],
    default: null,
  },
});

// The root crumb is an explicit link to the Configuration landing page — never router.back(),
// so a direct load of /settings/configuration/branding can still navigate back to the grid.
const breadcrumbItems = [
  { text: "Configuration", href: "/settings/configuration", disabled: false },
  { text: "Branding", disabled: true },
];

const uploadCloudSrc = require("@components/assets/upload-cloud-02.svg");

const fileInput = ref(null);
const logoUrl = ref(null);
const loading = ref(false);
const loadError = ref("");
const uploading = ref(false);
const removing = ref(false);
const isDragging = ref(false);
const showRemoveDialog = ref(false);
const uploadError = ref(null);
const pendingFile = ref(null);

const busy = computed(() => uploading.value || removing.value);

function resolveAccountId() {
  if (props.accountId !== null && props.accountId !== undefined && props.accountId !== "") {
    return props.accountId;
  }
  try {
    return JSON.parse(localStorage.getItem("currentAccountID"));
  } catch {
    return null;
  }
}

async function loadLogo() {
  const accountId = resolveAccountId();
  if (accountId === null || accountId === undefined) {
    loadError.value = "Could not determine the account to load the logo for.";
    return;
  }

  loading.value = true;
  loadError.value = "";
  try {
    const { data } = await getAccountData(accountId, { fresh: true });
    logoUrl.value = data?.logo_url || null;
  } catch {
    loadError.value = "Failed to load the account logo. Please try again.";
  } finally {
    loading.value = false;
  }
}

function isUsableLogo(file) {
  return (
    ACCEPTED_TYPES.includes(String(file.type).toLowerCase()) &&
    file.size > 0 &&
    file.size <= MAX_LOGO_BYTES
  );
}

function openFilePicker() {
  if (busy.value) return;
  fileInput.value?.click();
}

async function startUpload(file) {
  const accountId = resolveAccountId();
  if (accountId === null || accountId === undefined) {
    uploadError.value = { message: UPLOAD_FAILED_MESSAGE, retryable: true };
    return;
  }

  uploading.value = true;
  uploadError.value = null;
  try {
    logoUrl.value = await uploadAccountLogo(accountId, file);
    pendingFile.value = null;
  } catch {
    uploadError.value = { message: UPLOAD_FAILED_MESSAGE, retryable: true };
  } finally {
    uploading.value = false;
  }
}

function acceptFile(file) {
  isDragging.value = false;
  if (!file || busy.value) return;

  if (!isUsableLogo(file)) {
    pendingFile.value = null;
    uploadError.value = { message: INVALID_FILE_MESSAGE, retryable: false };
    return;
  }

  pendingFile.value = file;
  startUpload(file);
}

function handleFileInput(event) {
  const file = event?.target?.files?.[0];
  event.target.value = "";
  acceptFile(file);
}

function handleDrop(event) {
  acceptFile(event?.dataTransfer?.files?.[0]);
}

function retryUpload() {
  if (!pendingFile.value || busy.value) return;
  startUpload(pendingFile.value);
}

async function confirmRemove() {
  const accountId = resolveAccountId();
  if (accountId === null || accountId === undefined || removing.value) return;

  removing.value = true;
  uploadError.value = null;
  try {
    await deleteAccountLogo(accountId);
    logoUrl.value = null;
    pendingFile.value = null;
  } catch {
    uploadError.value = { message: REMOVE_FAILED_MESSAGE, retryable: false };
  } finally {
    removing.value = false;
    showRemoveDialog.value = false;
  }
}

onMounted(loadLogo);
</script>

<style scoped>
.branding-configuration {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.branding-configuration__breadcrumbs {
  width: 100%;
}

.branding-configuration__section {
  width: 100%;
  max-width: 1032px;
  padding: 32px;
}

.branding-configuration__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.branding-configuration__title {
  margin: 0;
  font-family: Inter;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
}

.branding-configuration__subtitle {
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.branding-configuration__divider {
  margin: 20px 0 0;
  border: none;
  border-top: 1px solid #eaecf0;
}

.branding-configuration__message {
  margin: 16px 0;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: Inter;
  font-size: 14px;
  line-height: 20px;
}

.branding-configuration__message--error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b42318;
}

.branding-configuration__row {
  display: flex;
  align-items: flex-start;
  gap: 64px;
  padding: 16px 0;
}

.branding-configuration__label-block {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 0 0 280px;
  max-width: 280px;
}

.branding-configuration__name {
  font-family: Inter;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #344054;
}

.branding-configuration__control {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 12px;
  max-width: 512px;
}

.branding-configuration__hint {
  margin: 0;
  font-family: Inter;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
}

.branding-configuration__dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 16px 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  cursor: pointer;
}

.branding-configuration__dropzone:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.branding-configuration__dropzone:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.branding-configuration__dropzone--dragover {
  border-color: #7f56d9;
  background: #f9f5ff;
}

.branding-configuration__dropzone--error {
  border-color: #d92d20;
}

.branding-configuration__dropzone-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.branding-configuration__dropzone-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-family: Inter;
  font-size: 14px;
  line-height: 20px;
}

.branding-configuration__dropzone-link {
  font-weight: 600;
  color: #6941c6;
}

.branding-configuration__dropzone-or {
  font-weight: 400;
  color: #475467;
}

.branding-configuration__dropzone-hint {
  font-family: Inter;
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  color: #475467;
  text-align: center;
}

.branding-configuration__current {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
}

.branding-configuration__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 160px;
  max-width: 100%;
  padding: 16px;
  box-sizing: border-box;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 12px;
}

.branding-configuration__preview-image {
  display: block;
  max-width: 100%;
  max-height: 96px;
  object-fit: contain;
}

.branding-configuration__actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.branding-configuration__feedback {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.branding-configuration__error-text {
  font-family: Inter;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #b42318;
}

.branding-configuration__file-input {
  display: none;
}
</style>
